import { useState } from 'react';
import { Save, Loader2, CheckCircle2, Lock } from 'lucide-react';
import { revealParametreAdmin } from '../services/api';
import ChampDepartement from './ChampDepartement';
import Aide from './Aide';
import { AIDES_CHAMPS } from '../utils/aides';

export interface ParametreItem {
  cle: string;
  label: string;
  valeur: string | null;
  valeurMasquee: boolean;
  estRenseigne: boolean;
  apercu: string | null;
  type: string;
  description: string | null;
  groupe: string | null;
  modifiable: boolean;
  /** Le bureau a déclaré que ce champ resterait vide. */
  laisseVierge?: boolean;
  reserveSuperAdmin: boolean;
}

/**
 * Certains paramètres se choisissent, ils ne se tapent pas : une liste fermée
 * évite la valeur inconnue que le serveur ignorerait en silence.
 */
const CHOIX: Record<string, { valeur: string; libelle: string }[]> = {
  CLOTURE_AUTOMATIQUE: [
    { valeur: 'annuelle', libelle: 'Clôture automatique au 31 décembre de chaque année' },
    { valeur: 'calendaire', libelle: 'Clôture calendaire : tous les douze mois à compter de la date de déclaration' },
    { valeur: 'desactivee', libelle: 'Désactivée — clôture à la main uniquement' },
  ],
};

/**
 * Champ d'edition d'un parametre global (texte, mot de passe, texte long,
 * booleen).
 *
 * Partage la logique entre l'ecran « Parametres » et l'ecran « DevOps » :
 * masquage des valeurs sensibles, bouton enregistrer desactive tant que rien
 * n'a change, etapercu des secrets deja poses.
 *
 * Pour un mot de passe, deux boutons distincts revelent ou masquent la valeur
 * en clair. La consultation d'un secret deja enregistre exige de ressaisir le
 * mot de passe de l'utilisateur connecte.
 */
const MOIS_FR: Record<string, number> = {
  janvier: 0, fevrier: 1, février: 1, mars: 2, avril: 3, mai: 4, juin: 5,
  juillet: 6, aout: 7, août: 7, septembre: 8, octobre: 9, novembre: 10, decembre: 11, décembre: 11,
};

/**
 * Lit une date telle que les mentions légales l'écrivent : « 25 juillet
 * 1968 », mais aussi 25/07/1968 ou 1968-07-25 — le champ est un texte libre,
 * la vie l'a rempli de plusieurs façons.
 */
function lireDateFr(brut: string): Date | null {
  const texte = brut.trim().toLowerCase().replace(/^le\s+/, '');

  const enLettres = texte.match(/^(\d{1,2})(?:er)?\s+([a-zéû]+)\s+(\d{4})$/);
  if (enLettres && MOIS_FR[enLettres[2]] !== undefined) {
    return new Date(Number(enLettres[3]), MOIS_FR[enLettres[2]], Number(enLettres[1]));
  }

  const francaise = texte.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (francaise) {
    return new Date(Number(francaise[3]), Number(francaise[2]) - 1, Number(francaise[1]));
  }

  const iso = texte.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }

  return null;
}

/**
 * La prochaine échéance calendaire : l'anniversaire de la déclaration qui
 * vient — cette année s'il n'est pas passé, sinon l'an prochain.
 */
function prochaineEcheanceCalendaire(dateDeclaration: string): string | null {
  const declaration = lireDateFr(dateDeclaration);
  if (declaration === null || Number.isNaN(declaration.getTime())) return null;

  const aujourdHui = new Date();
  const candidate = new Date(aujourdHui.getFullYear(), declaration.getMonth(), declaration.getDate());
  if (candidate <= aujourdHui) {
    candidate.setFullYear(candidate.getFullYear() + 1);
  }

  return candidate.toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris', day: 'numeric', month: 'long', year: 'numeric' });
}

export default function ParametreField({
  parametre: p,
  onSave,
  isSaving,
  isSaved,
  disabled = false,
  dateDeclaration = null,
  obligatoire = false,
  onLaisserVierge,
}: {
  parametre: ParametreItem;
  onSave: (cle: string, valeur: string | null) => void;
  isSaving: boolean;
  isSaved: boolean;
  disabled?: boolean;
  /** Date de déclaration de l'association : l'échéance calendaire s'en déduit. */
  dateDeclaration?: string | null;
  /**
   * Ce champ figure-t-il parmi les obligatoires ? Lui seul porte la case
   * « laisser vierge » : les autres ne sont réclamés par personne.
   */
  obligatoire?: boolean;
  /** Bascule la déclaration « ce champ restera vide ». */
  onLaisserVierge?: (cle: string, laisseVierge: boolean) => void;
}) {
  const [edited, setEdited] = useState<string | null>(null);
  const [show, setShow] = useState(false);
  const [revealOpen, setRevealOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [revealLoading, setRevealLoading] = useState(false);
  const [revealValue, setRevealValue] = useState<string | null>(null);
  const [revealError, setRevealError] = useState<string | null>(null);

  const isPassword = p.type === 'password' || p.cle.includes('SECRET') || p.cle.includes('TOKEN') || p.cle.includes('DSN');
  const isBoolean = p.type === 'boolean';
  // La valeur révélée prend la place de la valeur masquée dans le champ :
  // c'est là qu'on la cherche, et c'est là qu'on la corrige.
  const displayValue = p.valeurMasquee ? (revealValue ?? '') : (p.valeur ?? '');
  const currentInput = edited ?? displayValue;
  const modifiable = p.modifiable && !disabled;

  const handleSave = () => {
    if (isBoolean) {
      const nouvelle = p.valeur === '1' ? '0' : '1';
      onSave(p.cle, nouvelle);
      return;
    }
    // Rien n'a ete modifie : ne pas sauvegarder (et surtout ne pas effacer une
    // valeur sensible enregistree en envoyant null par erreur).
    if (edited === undefined || edited === null || edited === p.valeur) {
      return;
    }
    onSave(p.cle, p.valeurMasquee && edited.trim() === '' ? null : edited);
  };

  /**
   * Révèle la valeur enregistrée.
   *
   * On tente d'abord sans mot de passe : le super administrateur passe
   * toujours, et les autres passent aussi tant que la page est ouverte — le
   * serveur garde ce déverrouillage un quart d'heure. Le champ de mot de
   * passe n'apparaît donc qu'au premier refus.
   *
   * Le redemander à chaque champ faisait qu'on renonçait à vérifier : on
   * collait une clé sans jamais relire celle d'avant, et c'est ainsi qu'on
   * garde une clé de test en production.
   */
  const handleReveal = async (motDePasse = '') => {
    setRevealError(null);
    setRevealLoading(true);
    try {
      const data = await revealParametreAdmin(p.cle, motDePasse);
      setRevealValue(data.valeur ?? '');
      setPassword('');
      setRevealOpen(false);
      setShow(true);

      return true;
    } catch (err: any) {
      // Premier essai à vide : ce n'est pas une erreur à montrer, c'est le
      // signal qu'il faut demander le mot de passe.
      if (motDePasse === '') {
        setRevealOpen(true);
      } else {
        setRevealError(err?.message ?? 'Impossible de révéler la valeur.');
      }

      return false;
    } finally {
      setRevealLoading(false);
    }
  };

  /**
   * Le 👀 sur un champ masqué : il révèle, il ne dévoile pas un champ vide.
   *
   * Une valeur sensible n'est jamais dans le formulaire — l'API ne la renvoie
   * pas. Basculer le type du champ de « password » à « text » ne montrait
   * donc rien du tout, et l'œil paraissait cassé.
   */
  const toggleShow = () => {
    if (p.valeurMasquee && p.estRenseigne && revealValue === null) {
      void handleReveal();

      return;
    }
    setShow(true);
  };
  const toggleHide = () => {
    setShow(false);
    setRevealOpen(false);
    setRevealValue(null);
    setRevealError(null);
  };

  return (
    <div className={`space-y-1 p-3 rounded-xl border-2 transition-colors ${isSaved ? 'border-green-300 bg-green-50' : 'border-transparent'}`}>
      <div className="flex flex-wrap items-center gap-x-2">
        <label htmlFor={p.cle} className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {p.label}
          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">{p.cle}</span>
          {p.reserveSuperAdmin && (
            <span className="ml-2 inline-flex items-center gap-1 text-xs font-normal px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
              <Lock className="w-3 h-3" /> Super admin
            </span>
          )}
          {isSaved && (
            <span className="ml-2 inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-800">
              <CheckCircle2 className="w-3 h-3" /> Enregistré
            </span>
          )}
        </label>
        {/* Le « i » n'apparaît que là où le libellé ne suffit pas : en poser
            un sur « Nom de l'association » n'apprendrait rien et rendrait
            suspects tous les autres. */}
        {AIDES_CHAMPS[p.cle] && <Aide titre={p.label}>{AIDES_CHAMPS[p.cle]}</Aide>}
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {p.description}
        {p.reserveSuperAdmin && ' — modifiable uniquement par un super administrateur.'}
      </p>

      <div className="flex gap-2">
        {isBoolean ? (
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer flex-1 dark:text-gray-300">
            <input
              type="checkbox"
              checked={p.valeur === '1'}
              onChange={handleSave}
              disabled={!modifiable}
              className="w-4 h-4"
            />
            {p.valeur === '1' ? 'Activé' : 'Désactivé'}
          </label>
        ) : CHOIX[p.cle] ? (
          <div className="flex-1 space-y-1">
            <select
              id={p.cle}
              value={currentInput ?? ''}
              onChange={(e) => setEdited(e.target.value)}
              disabled={!modifiable}
              className="input w-full"
            >
              {CHOIX[p.cle].map((choix) => (
                <option key={choix.valeur} value={choix.valeur}>{choix.libelle}</option>
              ))}
            </select>
            {/* En calendaire, la date qui compte s'affiche sous le choix :
                on sait ce qu'on signe avant d'enregistrer. */}
            {p.cle === 'CLOTURE_AUTOMATIQUE' && (currentInput ?? '') === 'calendaire' && (
              <p className="text-xs text-primary-700 dark:text-primary-300">
                {dateDeclaration && prochaineEcheanceCalendaire(dateDeclaration)
                  ? `Prochaine clôture : le ${prochaineEcheanceCalendaire(dateDeclaration)} (anniversaire de la déclaration du ${lireDateFr(dateDeclaration)!.toLocaleDateString('fr-FR')}).`
                  : 'Renseignez d\'abord la date de déclaration de l\'association (ASSOCIATION_DATE_DECLARATION, section Mentions légales) : c\'est elle qui fixe l\'échéance.'}
              </p>
            )}
          </div>
        ) : p.type === 'departement' ? (
          <ChampDepartement
            id={p.cle}
            valeur={currentInput ?? ''}
            onChange={(valeur) => setEdited(valeur)}
            disabled={!modifiable}
          />
        ) : p.type === 'textarea' ? (
          <textarea
            id={p.cle}
            value={currentInput ?? ''}
            onChange={(e) => setEdited(e.target.value)}
            disabled={!modifiable}
            rows={3}
            className="input flex-1 min-h-[80px]"
          />
        ) : (
          <div className="relative flex-1">
            <input
              id={p.cle}
              type={isPassword && !show ? 'password' : 'text'}
              value={currentInput ?? ''}
              onChange={(e) => {
                setEdited(e.target.value);
                if (isPassword) {
                  setRevealValue(null);
                  setRevealError(null);
                }
              }}
              disabled={!modifiable}
              placeholder={
                p.valeurMasquee
                  ? (p.estRenseigne ? `Actuel : ${p.apercu} — saisissez pour remplacer` : '(non configuré)')
                  : ''
              }
              className="input w-full pr-20"
            />
            {/* Deux boutons plutôt qu'une bascule : l'emoji dit l'état
                courant, pas l'action à venir, et un bouton unique laisserait
                chacun l'interpréter dans son sens. Ici 👀 affiche et 🙈
                masque ; celui qui est en relief est celui qu'on peut faire. */}
            {isPassword && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                <button
                  type="button"
                  onClick={toggleShow}
                  title="Afficher la valeur saisie"
                  aria-label="Afficher la valeur saisie"
                  className={`p-0.5 text-base leading-none transition-opacity ${
                    show ? 'opacity-40' : 'opacity-100 hover:scale-110'
                  }`}
                  tabIndex={-1}
                >
                  <span aria-hidden="true">👀</span>
                </button>
                <button
                  type="button"
                  onClick={toggleHide}
                  title="Masquer la valeur saisie"
                  aria-label="Masquer la valeur saisie"
                  className={`p-0.5 text-base leading-none transition-opacity ${
                    show ? 'opacity-100 hover:scale-110' : 'opacity-40'
                  }`}
                  tabIndex={-1}
                >
                  <span aria-hidden="true">🙈</span>
                </button>
              </div>
            )}
          </div>
        )}

        {!isBoolean && (
          <button
            type="button"
            onClick={handleSave}
            title={p.valeurMasquee ? 'La valeur reste masquée après enregistrement' : undefined}
            disabled={!modifiable || (edited === undefined || edited === null || edited === p.valeur) || isSaving}
            className="btn-primary px-3 disabled:opacity-50 inline-flex items-center gap-1"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : isSaved ? <CheckCircle2 className="w-4 h-4" /> : <Save className="w-4 h-4" />}
            <span className="hidden sm:inline">{isSaved ? 'Enregistré' : 'Enregistrer'}</span>
          </button>
        )}
      </div>

      {/* Consultation d'un secret deja enregistre */}
      {isPassword && p.estRenseigne && (
        <div className="pt-1">
          <button
            type="button"
            onClick={() => (revealValue !== null ? toggleHide() : toggleShow())}
            disabled={revealLoading}
            className="inline-flex items-center gap-1 text-xs text-primary-700 hover:underline disabled:opacity-50"
          >
            {revealLoading ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <span aria-hidden="true">{revealValue !== null ? '🙈' : '👀'}</span>
            )}
            {revealValue !== null ? 'Masquer' : 'Voir la valeur enregistrée'}
          </button>

          {/* Le mot de passe n'est demandé qu'au premier refus du serveur :
              une fois donné, il ouvre la page un quart d'heure. */}
          {revealOpen && (
            <div className="mt-2 flex flex-col sm:flex-row gap-2 items-start sm:items-center">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void handleReveal(password); }}
                placeholder="Votre mot de passe"
                className="input text-sm py-1 flex-1"
                autoFocus
              />
              <button
                type="button"
                onClick={() => void handleReveal(password)}
                disabled={revealLoading || password.trim() === ''}
                className="btn-primary px-3 py-1 text-sm inline-flex items-center gap-1 disabled:opacity-50"
              >
                {revealLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <span aria-hidden="true">👀</span>}
                Révéler
              </button>
            </div>
          )}

          {revealError && <p className="text-xs text-red-600 mt-1">{revealError}</p>}
          {revealValue !== null && (
            <code className="block mt-1 p-2 bg-gray-50 border rounded text-xs font-mono break-all">{revealValue}</code>
          )}
        </div>
      )}

      {/* « Laisser vierge » : toutes les associations n'ont pas de numéro RNA,
          de parution au Journal officiel ni de compte bancaire. Cocher éteint
          la demande sans rien inventer dans le champ, et se décoche le jour
          où l'information arrive. */}
      {obligatoire && onLaisserVierge && (
        <label className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 cursor-pointer select-none">
          <input
            type="checkbox"
            className="w-3.5 h-3.5 accent-gray-400"
            checked={p.laisseVierge === true}
            onChange={(e) => onLaisserVierge(p.cle, e.target.checked)}
            disabled={disabled}
          />
          Laisser vierge — nous n'avons pas cette information
        </label>
      )}
    </div>
  );
}
